import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { getJobPositionByID } from '../../../api/job-position.js';
import { getOrCalculateMatchingScore } from '../../../api/matching-score.js';
import { MatchingScore } from '../../../db/MatchingScore.js';
import { getMatchingScoreText } from '../../../features/matching-score/utils.js';
import { CtaPillButton } from '../../components/cta-pill-button.js';
import { useScrollToError } from '../../components/format-form-error.js';

export const RegisterOnboardingMatchingScore = stateful(
  ({
    onFinished,
    onBack,
    jobIDs: jobIDsProp,
  }: {
    onFinished: () => void;
    onBack: () => void;
    // Explicit job set for this step instance. The step stays mounted (hidden) after
    // navigation, and step 6 rewrites the ?jobs= param - following the live URL here would
    // refire LLM scoring in the background and show the wrong job set on back-navigation.
    jobIDs?: string[];
  }) => {
    const [confirmTruth, setConfirmTruth] = useState(false);
    const [scoreLoaded, setScoreLoaded] = useState(false);
    const [retryCounter, setRetryCounter] = useState(0);
    const [confirmTruthError, setConfirmTruthError] = useState<string | null>(null, []);
    useScrollToError(confirmTruthError);

    // Job IDs from the explicit prop, falling back to the URL (?jobs=<id1,id2,...>)
    const jobIDs =
      jobIDsProp ??
      (routingStateSignal.value.searchParams.get('jobs') || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
    const user = useObservable(() => currentUser<User>(), []);

    type JobScore = {
      jobId: string;
      score: MatchingScore | null;
      job: { title?: string; company?: { name?: string } } | null;
    };

    const scoreQuery = useQuery(async () => {
      if (!user) return [] as JobScore[];
      if (!jobIDs.length) {
        // Nothing to score - mark loading finished so the continue CTA is not stuck disabled.
        setScoreLoaded(true);
        return [] as JobScore[];
      }
      try {
        const results = await Promise.all(
          jobIDs.map(async jobId => {
            let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
            try {
              const timeoutPromise = new Promise<never>((_, reject) => {
                timeoutHandle = setTimeout(
                  () => reject(new Error('Matching score timed out for job ' + jobId)),
                  100_000,
                );
              });
              const [score, job] = await Promise.race([
                Promise.all([
                  getOrCalculateMatchingScore(user._id, jobId)(selectionSet(() => MatchingScore, 2)),
                  getJobPositionByID(jobId)({
                    title: 1,
                    company: { name: 1 },
                  } as const),
                ]),
                timeoutPromise,
              ]);
              return {
                jobId,
                score,
                job: (job as unknown as { title?: string; company?: { name?: string } }) ?? null,
              };
            } catch (e) {
              console.error('Failed to load matching score for job', jobId, e);
              return { jobId, score: null, job: null };
            } finally {
              if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
            }
          }),
        );
        setScoreLoaded(true);
        return results;
      } catch (e) {
        console.error('Failed to load matching scores', e);
        setScoreLoaded(true);
        return [] as JobScore[];
      }
    }, [user?._id, jobIDs.join(','), retryCounter]);

    // Using the extracted utility function for consistency

    return html`
      <div class="space-y-4">
        <div class="card bg-base-100">
          <div class="card-body">
            <h3 class="card-title">${m.matching_score_calculation_title()}</h3>
            <p class="text-sm opacity-80">${m.matching_score_calculation_intro()}</p>
            ${
              !scoreLoaded && scoreQuery.loading
                ? html`<div class="flex items-center gap-3 mt-3">
                    <span class="loading loading-spinner loading-md"></span>
                    <span class="opacity-70 text-sm">${m.matching_score_calculating()}</span>
                  </div>`
                : html``
            }
          </div>
        </div>

        ${
          scoreLoaded
            ? html`<div class="card bg-base-100">
                <div class="card-body space-y-4">
                  <div class="font-medium">
                    ${
                      jobIDs.length > 1
                        ? m.matching_score_result_heading_plural()
                        : m.matching_score_result_heading_singular()
                    }
                  </div>
                  ${
                    scoreQuery.value && scoreQuery.value.length
                      ? html`
                          <div class="space-y-4">
                            ${scoreQuery.value.map(
                              ({ jobId, score, job }, i) =>
                                html`<div class="rounded-box">
                                  <div class="text-sm opacity-80 mb-2">
                                    <span class="text-lg font-bold">${job?.title ?? m.unknown_job_title()}</span>
                                    <span class="opacity-60"> – ${job?.company?.name ?? m.unknown_company()}</span>
                                  </div>
                                  ${
                                    score
                                      ? html`
                                          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                            <div class="font-semibold">${m.overall_score_label()}</div>
                                            <div>
                                              ${(() => {
                                                const t = score.total ?? 0;
                                                const cls =
                                                  t >= 70 ? 'badge-success' : t >= 40 ? 'badge-warning' : 'badge-error';
                                                return html`<span class="badge ${cls}" aria-label="Score-Level">
                                                  ${getMatchingScoreText(t)}
                                                </span>`;
                                              })()}
                                            </div>
                                            <div>${m.matching_score_category_professional_fit()}</div>
                                            <div>
                                              ${(() => {
                                                const t = score.rawSkills ?? 0;
                                                const cls =
                                                  t >= 0.7
                                                    ? 'badge-success'
                                                    : t >= 0.4
                                                      ? 'badge-warning'
                                                      : 'badge-error';
                                                return html`<span
                                                  class="badge ${cls}"
                                                  aria-label="${m.matching_score_category_professional_fit()}"
                                                  >${getMatchingScoreText(t * 100)}</span
                                                >`;
                                              })()}
                                            </div>
                                            <div>${m.matching_score_category_language_skills()}</div>
                                            <div>
                                              ${(() => {
                                                const t = score.rawLanguages ?? 0;
                                                const cls =
                                                  t >= 0.7
                                                    ? 'badge-success'
                                                    : t >= 0.4
                                                      ? 'badge-warning'
                                                      : 'badge-error';
                                                return html`<span
                                                  class="badge ${cls}"
                                                  aria-label="${m.matching_score_category_language_skills()}"
                                                  >${getMatchingScoreText(t * 100)}</span
                                                >`;
                                              })()}
                                            </div>
                                            <div>${m.matching_score_category_soft_skills()}</div>
                                            <div>
                                              ${(() => {
                                                const t = score.rawSoftSkills ?? 0;
                                                const cls =
                                                  t >= 0.7
                                                    ? 'badge-success'
                                                    : t >= 0.4
                                                      ? 'badge-warning'
                                                      : 'badge-error';
                                                return html`<span
                                                  class="badge ${cls}"
                                                  aria-label="${m.matching_score_category_soft_skills()}"
                                                  >${getMatchingScoreText(t * 100)}</span
                                                >`;
                                              })()}
                                            </div>
                                            <div>${m.matching_score_category_practical_experience()}</div>
                                            <div>
                                              ${(() => {
                                                const t = score.rawExperience ?? 0;
                                                const cls =
                                                  t >= 0.7
                                                    ? 'badge-success'
                                                    : t >= 0.4
                                                      ? 'badge-warning'
                                                      : 'badge-error';
                                                return html`<span
                                                  class="badge ${cls}"
                                                  aria-label="${m.matching_score_category_practical_experience()}"
                                                  >${getMatchingScoreText(t * 100)}</span
                                                >`;
                                              })()}
                                            </div>
                                            <div>${m.matching_score_category_role_fit()}</div>
                                            <div>
                                              ${(() => {
                                                const t = score.rawPosition ?? 0;
                                                const cls =
                                                  t >= 0.7
                                                    ? 'badge-success'
                                                    : t >= 0.4
                                                      ? 'badge-warning'
                                                      : 'badge-error';
                                                return html`<span
                                                  class="badge ${cls}"
                                                  aria-label="${m.matching_score_category_role_fit()}"
                                                  >${getMatchingScoreText(t * 100)}</span
                                                >`;
                                              })()}
                                            </div>
                                          </div>
                                          ${
                                            (score.notes ?? []).length
                                              ? html`<div class="mt-2">
                                                  <div class="text-sm font-semibold">${m.notes_label()}</div>
                                                  <ul class="list-disc ml-5 text-sm opacity-80">
                                                    ${(score.notes ?? []).map(n => html`<li>${n}</li>`)}
                                                  </ul>
                                                </div>`
                                              : html``
                                          }
                                          ${i < (scoreQuery.value?.length ?? 0) - 1 ? html`<hr class="my-2" />` : html``}
                                        `
                                      : html`<div class="alert alert-warning text-sm">
                                          <span>${m.no_score_available()}</span>
                                        </div>`
                                  }
                                </div>`,
                            )}
                            ${
                              scoreLoaded && scoreQuery.value.some(({ score }) => score === null)
                                ? html`<div class="alert alert-warning text-sm">
                                    <span>${m.matching_score_partial_error()}</span>
                                    <button
                                      class="btn btn-sm"
                                      @click=${() => {
                                        setScoreLoaded(false);
                                        setRetryCounter(retryCounter + 1);
                                      }}
                                    >
                                      ${m.matching_score_retry_button()}
                                    </button>
                                  </div>`
                                : nothing
                            }
                          </div>
                        `
                      : html`<div class="alert alert-warning text-sm">
                          <span>${m.no_jobs_provided()}</span>
                        </div>`
                  }
                </div>
              </div>`
            : html``
        }

        <div class="card bg-base-100">
          <div class="card-body space-y-3">
            <div class="font-medium">${m.confirm_submit_application_title()}</div>
            <div class="text-sm opacity-70">${m.confirm_submit_application_note()}</div>
            <label class="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                class="checkbox mt-1"
                .checked=${confirmTruth}
                @change=${(e: Event) => {
                  const checked = (e.target as HTMLInputElement).checked;
                  setConfirmTruth(checked);
                  if (checked) setConfirmTruthError(null);
                }}
              />
              <span>
                ${m.register_onboarding_terms_form_accuracyConfirm_label()}<span class="text-error"> *</span>
              </span>
            </label>
            ${
              confirmTruthError
                ? html`<div class="alert alert-error" data-error-anchor>
                    <span class="icon-[material-symbols--error]"></span>
                    <span>${confirmTruthError}</span>
                  </div>`
                : nothing
            }
          </div>
        </div>

        <div class="mt-6 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-2">
          <button class="btn w-full sm:w-auto" @click=${() => onBack()}>${m.back()}</button>
          ${CtaPillButton({
            label: m.register_onboarding_motivation_save_and_next_button(),
            onClick: () => {
              if (!confirmTruth) {
                setConfirmTruthError(m.register_onboarding_confirm_truth_error());
                return;
              }
              setConfirmTruthError(null);
              onFinished();
            },
            disabled: !scoreLoaded || !(scoreQuery.value ?? []).every(({ score }) => score !== null),
          })}
        </div>
      </div>
    `;
  },
);
